import React, { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HEALTH_ITEMS } from './enrollmentHealthModel';

export const REQUIREMENT_SECTIONS = [
  { id: 'aluno', label: 'Aluno', title: 'Dados do aluno', description: 'Identificação, endereço e dados escolares.' },
  { id: 'filiacao', label: 'Filiação', title: 'Filiação', description: 'Dados do pai e da mãe, reutilizados nas autorizações.' },
  { id: 'financeiro', label: 'Financeiro', title: 'Responsável financeiro', description: 'Defina quem responde financeiramente pelo aluno.' },
  { id: 'pedagogico', label: 'Pedagógico', title: 'Responsável pedagógico', description: 'Defina o responsável pelo acompanhamento escolar.' },
  { id: 'saude', label: 'Saúde', title: 'Aspectos de saúde', description: 'Respostas rápidas e detalhes somente quando necessários.' },
  { id: 'autorizacoes', label: 'Autorizações', title: 'Autorizações', description: 'Retirada do aluno e autorização de imagem e voz.' },
  { id: 'complementares', label: 'Complementares', title: 'Pagamento e dados complementares', description: 'Forma de pagamento, situação do contrato e censo escolar.' },
  { id: 'documentos', label: 'Assinaturas', title: 'Contrato e assinaturas', description: 'Importe o contrato e recolha as assinaturas digitais.' },
];

const healthFields = new Set(HEALTH_ITEMS.flatMap(item => [item.field, item.detail, item.treatment].filter(Boolean)));
export const sectionForRequirementField = field => {
  if (healthFields.has(field)) return 'saude';
  if (['retiradaPai', 'retiradaMae'].includes(field)) return 'filiacao';
  if (/^(retirada|saidaAcompanhada)/.test(field) || field === 'autorizaDireitoImagem') return 'autorizacoes';
  if (/(Pai|Mae)$/.test(field)) return 'filiacao';
  if (field === 'responsavelFinanceiro' || /Responsavel$/.test(field)) return 'financeiro';
  if (field === 'responsavelPedagogico') return 'pedagogico';
  if (['formaPagamento', 'corRaca'].includes(field)) return 'complementares';
  return 'aluno';
};

export const nextRequiredSection = (sectionId, errors) => {
  const index = REQUIREMENT_SECTIONS.findIndex(section => section.id === sectionId);
  const ordered = [...REQUIREMENT_SECTIONS.slice(index + 1), ...REQUIREMENT_SECTIONS.slice(0, index)];
  return ordered.find(section => Object.keys(errors).some(field => sectionForRequirementField(field) === section.id))?.id || null;
};

export function RequerimentoSection({ sectionId, pending, children, onComplete, openRequest = 0 }) {
  const [expanded, setExpanded] = useState(pending > 0);
  const previousPending = useRef(pending);
  const editing = useRef(false);
  const changed = useRef(false);
  const readyToComplete = useRef(false);
  const choiceCommitted = useRef(false);
  const completeCallback = useRef(onComplete);
  completeCallback.current = onComplete;
  const complete = () => {
    readyToComplete.current = false;
    changed.current = false;
    choiceCommitted.current = false;
    editing.current = false;
    setExpanded(false);
    completeCallback.current?.(sectionId);
  };
  const completeRef = useRef(complete);
  completeRef.current = complete;
  useEffect(() => {
    if (openRequest) setExpanded(true);
  }, [openRequest]);
  useEffect(() => {
    if (previousPending.current > 0 && pending === 0) {
      readyToComplete.current = changed.current;
      if (readyToComplete.current && (!editing.current || choiceCommitted.current)) completeRef.current();
      else if (!editing.current) setExpanded(false);
    }
    if (previousPending.current === 0 && pending > 0) {
      readyToComplete.current = false;
      choiceCommitted.current = false;
      setExpanded(true);
    }
    previousPending.current = pending;
  }, [pending]);
  const finishEditing = event => {
    if (!pending && readyToComplete.current) { complete(); return; }
    if (event.currentTarget.contains(event.relatedTarget)) return;
    editing.current = false;
    if (!pending) setExpanded(false);
  };
  const trackChange = event => {
    editing.current = true;
    changed.current = true;
    choiceCommitted.current = event.target.tagName === 'SELECT' || ['radio', 'checkbox'].includes(event.target.type);
  };
  const trackChoice = event => {
    if (!event.target.closest?.('[role="option"], button[value]')) return;
    editing.current = true;
    changed.current = true;
    choiceCommitted.current = true;
  };
  const finishWithEnter = event => {
    if (event.key !== 'Enter' || event.target.tagName === 'TEXTAREA' || event.nativeEvent.isComposing) return;
    if (!pending && readyToComplete.current) { event.preventDefault(); complete(); }
  };
  const section = REQUIREMENT_SECTIONS.find(item => item.id === sectionId);
  return <Accordion component="section" id={`requerimento-${sectionId}`} expanded={expanded} onChange={(_, value) => setExpanded(value)}
    disableGutters elevation={0} sx={{ border: '1px solid #e1e8ef', borderRadius: '12px !important', mb: 1.5, scrollMarginTop: '85px', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} id={`requerimento-${sectionId}-heading`} aria-controls={`requerimento-${sectionId}-content`} sx={{ px: { xs: 2, sm: 2.5 }, py: 1 }}>
      <Box flex={1} display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" pr={1}>
        <Box><Typography component="h2" variant="h6" fontSize={18} fontWeight={700} color="#153a57">{section.title}</Typography>
          <Typography variant="caption" color="text.secondary">{section.description}</Typography></Box>
        <Chip size="small" variant="outlined" color={pending ? 'warning' : 'default'} label={pending ? `${pending} a completar` : sectionId === 'documentos' ? 'Documento e assinaturas' : 'Sem campos pendentes'} />
      </Box>
    </AccordionSummary>
    <AccordionDetails onFocusCapture={() => { editing.current = true; }} onChangeCapture={trackChange} onClickCapture={trackChoice} onKeyDownCapture={finishWithEnter} onBlurCapture={finishEditing} id={`requerimento-${sectionId}-content`} sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.5 }}>{children}</AccordionDetails>
  </Accordion>;
}
